import * as React from 'react';
import { IntegrationCapability, RunwayResponseType } from '../types';
import IntegrationIcon from './icons/IntegrationIcon';
import { runwayColors } from './styles/globalStyles';
import { EllipsisOutlined } from '@ant-design/icons';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { useSelector } from 'react-redux';
import * as API from '../api';
import Button, { ButtonProps } from 'antd/lib/button';
import { RootState } from '../reducers/reducers';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import { globalMessage } from '../utils/globalMessage';
import { selectIntegrationForCapability, selectJoinedCommsProvidersNamesList } from '../selectors/selectors';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface PingButtonProps extends ButtonProps, ReleaseComponentProps {
    checklistItemId: string;
    text: string;
    disabled: boolean;
    missingIntegration: boolean;
    disablePopover: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

export interface PingChecklistItemOwnersRequest {
    orgId: string;
    appId: string;
    releaseId: string;
    checklistItemId: string;
}

const PingButton: React.FC<PingButtonProps> = (props: PingButtonProps) => {
    const { orgId, appId, releaseId, checklistItemId, text, missingIntegration, ...rest } = props;

    const [isPingChecklistItemOwnersLoading, setIsPingChecklistItemOwnersLoading] = React.useState(false);
    const [popoverVisible, setPopoverVisible] = React.useState(false);

    const auth = useSelector((state: RootState) => state.auth);
    const authCredentials = auth.authCredentials;
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );

    const [
        pingChecklistItemOwners,
        pingChecklistItemOwnersIsLoading,
        pingChecklistItemOwnersResponseType,
        pingChecklistItemOwnersError,
    ] = useCallUpdatable(StepRunUpdatable.PingChecklistItem, { ...props.stepRunComponentProps, checklistItemId });

    React.useEffect(() => {
        if (pingChecklistItemOwnersResponseType === RunwayResponseType.Success) {
            globalMessage.success('Ping sent!');
            setPopoverVisible(false);
        }
    }, [pingChecklistItemOwnersResponseType]);

    React.useEffect(() => {
        if (pingChecklistItemOwnersError) {
            globalMessage.error(pingChecklistItemOwnersError);
        }
    }, [pingChecklistItemOwnersError]);

    const pingCliOwners = () => {
        const requestData: PingChecklistItemOwnersRequest = {
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
            checklistItemId: checklistItemId,
        };

        if (props.stepRunComponentProps) {
            pingChecklistItemOwners();
            return;
        }

        setIsPingChecklistItemOwnersLoading(true);

        API.pingChecklistItemOwners(requestData, authCredentials)
            .then(() => {
                globalMessage.success('Ping sent!');
                setPopoverVisible(false);

                return;
            })
            .catch((e) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => setIsPingChecklistItemOwnersLoading(false));
    };

    const renderPingButton = () => {
        const button = (
            <Button
                icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
                {...rest}
                disabled={props.disabled}
                onClick={pingCliOwners}
                loading={isPingChecklistItemOwnersLoading || pingChecklistItemOwnersIsLoading}
            >
                <span style={{ paddingLeft: 4 }}>{text}</span>
            </Button>
        );

        if (missingIntegration) {
            return (
                <Tooltip title={`Connect ${commsProviders} to ping owners about incomplete items`} placement="topRight">
                    {button}
                </Tooltip>
            );
        }

        return button;
    };

    const ellipsisButton = (
        <Button
            icon={<EllipsisOutlined style={{ color: runwayColors.primary, fontSize: 24 }} />}
            type="link"
            disabled={props.disablePopover}
        />
    );

    return props.disablePopover ?
            <>{ellipsisButton}</>
        :   <Popover
                content={renderPingButton()}
                open={popoverVisible}
                trigger="click"
                onVisibleChange={(open) => setPopoverVisible(open)}
            >
                <>{ellipsisButton}</>
            </Popover>;
};

export default PingButton;
